import type React from 'react';
import { useMemo, useState } from 'react';
import axios from 'axios';
import { Delete, Loader2, Phone } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';

import { addConversationToCustomerConversations } from '@/actions/webchat';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import type { RootState } from '@/reducers';
import { setCustomer, setStatus, setVisible } from '@/reducers/voiceReducer';
import { isCustomerOnActiveCall } from '@/utils/activeCustomerCall';
import formatPhoneForAPI from '@/utils/formatPhoneForAPI';
import formatPhoneForUser from '@/utils/formatPhoneForUser';
import { VoiceAgreementBanner } from '@/components/voice/VoiceAgreementBanner';

type VoiceDialerDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
};

type AuthState = {
	_id?: string;
	products?: string[];
	twilioNumber?: string;
	user?: {
		_business?: string;
		_temporaryBusiness?: string;
	};
};

type AppDispatch = (action: unknown) => unknown;

const KEYPAD_ROWS = [
	[
		{ digit: '1', letters: '' },
		{ digit: '2', letters: 'ABC' },
		{ digit: '3', letters: 'DEF' },
	],
	[
		{ digit: '4', letters: 'GHI' },
		{ digit: '5', letters: 'JKL' },
		{ digit: '6', letters: 'MNO' },
	],
	[
		{ digit: '7', letters: 'PQRS' },
		{ digit: '8', letters: 'TUV' },
		{ digit: '9', letters: 'WXYZ' },
	],
	[
		{ digit: '*', letters: '' },
		{ digit: '0', letters: '+' },
		{ digit: '#', letters: '' },
	],
] as const;

const ACTIVE_CALL_STATUSES = new Set(['connecting', 'ringing', 'answered']);

const getDigits = (value: string) => value.replace(/\D/g, '');

type CallingRegion = 'northAmerica' | 'australia' | 'unknown';

const getCallingRegion = (twilioNumber?: string): CallingRegion => {
	const rawNumber = (twilioNumber || '').trim();
	const digits = getDigits(rawNumber);

	if (rawNumber.startsWith('+1') || digits.startsWith('1')) {
		return 'northAmerica';
	}

	if (rawNumber.startsWith('+61') || digits.startsWith('61')) {
		return 'australia';
	}

	return 'unknown';
};

const isValidNorthAmericanNumber = (digits: string) => {
	const localNumber =
		digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

	if (localNumber.length !== 10) return false;

	const areaCodeStartsValid = /^[2-9]/.test(localNumber);
	const exchangeStartsValid = /^[2-9]/.test(localNumber.slice(3));

	return areaCodeStartsValid && exchangeStartsValid;
};

const isValidAustralianNumber = (digits: string) => {
	if (digits.length === 11 && digits.startsWith('61')) {
		const localNumber = `0${digits.slice(2)}`;
		return /^0[23478]\d{8}$/.test(localNumber);
	}

	if (digits.length === 10) {
		return /^0[23478]\d{8}$/.test(digits);
	}

	if (digits.length === 9 && digits.startsWith('4')) {
		return /^4\d{8}$/.test(digits);
	}

	return false;
};

const getPhoneValidationMessage = (
	value: string,
	callingRegion: CallingRegion
) => {
	const digits = getDigits(value);

	if (!digits) return 'Enter a phone number to call.';

	if (
		(callingRegion === 'northAmerica' || callingRegion === 'unknown') &&
		isValidNorthAmericanNumber(digits)
	) {
		return '';
	}

	if (
		(callingRegion === 'australia' || callingRegion === 'unknown') &&
		isValidAustralianNumber(digits)
	) {
		return '';
	}

	if (callingRegion === 'northAmerica') {
		return 'Enter a valid 10-digit US or Canadian phone number.';
	}

	if (callingRegion === 'australia') {
		return 'Enter a valid Australian phone number.';
	}

	return 'Enter a valid US, Canadian, or Australian phone number.';
};

const normalizeDialedNumber = (value: string) => {
	return formatPhoneForAPI(value).replace(/[^\d+]/g, '');
};

export function VoiceDialerDialog({ open, onOpenChange }: VoiceDialerDialogProps) {
	const dispatch = useDispatch() as AppDispatch;
	// Phones: a full-screen dialer whose own keypad replaces the system keyboard.
	const isMobile = useIsMobile();
	const auth = useSelector((state: RootState) => state.auth as AuthState);
	const voiceStatus = useSelector((state: RootState) => state.voice.status);
	const ongoingCalls = useSelector(
		(state: RootState) => state.voice.ongoingCalls,
	);
	const [dialedNumber, setDialedNumber] = useState('');
	const [isCalling, setIsCalling] = useState(false);
	const [validationMessage, setValidationMessage] = useState('');

	const hasVoiceProduct = auth?.products?.includes('voice');
	const customerCallInProgress = useMemo(
		() =>
			isCustomerOnActiveCall(ongoingCalls, {
				phone: normalizeDialedNumber(dialedNumber),
			}),
		[dialedNumber, ongoingCalls],
	);
	const isCallInProgress =
		ACTIVE_CALL_STATUSES.has(voiceStatus) || customerCallInProgress;
	const callingRegion = useMemo(
		() => getCallingRegion(auth?.twilioNumber),
		[auth?.twilioNumber]
	);

	const displayNumber = useMemo(
		() => {
			if (dialedNumber === '+') return '+';
			return dialedNumber ? formatPhoneForUser(dialedNumber) : '';
		},
		[dialedNumber]
	);

	const resetDialer = () => {
		setDialedNumber('');
		setIsCalling(false);
		setValidationMessage('');
	};

	const handleOpenChange = (nextOpen: boolean) => {
		if (!nextOpen && !isCalling) {
			resetDialer();
		}
		onOpenChange(nextOpen);
	};

	const handleNumberChange = (value: string) => {
		const sanitized = value
			.replace(/[^\d+#*+]/g, '')
			.replace(/(?!^)\+/g, '');
		setDialedNumber(sanitized);
		setValidationMessage('');
	};

	const handleKeyPress = (digit: string) => {
		setDialedNumber((previous) => `${previous}${digit}`);
		setValidationMessage('');
	};

	const handleBackspace = () => {
		setDialedNumber((previous) => previous.slice(0, -1));
		setValidationMessage('');
	};

	const handleDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== 'Enter' || event.repeat) return;
		if ((event.target as HTMLElement).closest('button')) return;
		if (!dialedNumber || isCalling || isCallInProgress) return;

		event.preventDefault();
		void handleStartCall();
	};

	const handleStartCall = async () => {
		if (isCalling) return;

		if (!hasVoiceProduct) {
			toast.error('Voice calling is not enabled for this account.');
			return;
		}

		if (customerCallInProgress) {
			toast.error(
				'This customer is already on a call with another teammate.',
			);
			return;
		}

		if (isCallInProgress) {
			toast.error('Finish the current call before starting another one.');
			return;
		}

		const phoneValidationMessage = getPhoneValidationMessage(
			dialedNumber,
			callingRegion
		);
		if (phoneValidationMessage) {
			setValidationMessage(phoneValidationMessage);
			return;
		}

		const normalizedPhone = normalizeDialedNumber(dialedNumber);
		const _location =
			auth?.user?._temporaryBusiness || auth?.user?._business || auth?._id;

		setIsCalling(true);

		try {
			const response = await axios.post('/api/create-customer', {
				customer: {
					name: '',
					phoneEmail: normalizedPhone,
					phone: normalizedPhone,
					_location,
				},
			});

			if (response.data?.error || !response.data?.customerConversation) {
				toast.error(
					response.data?.message ||
						'Unable to create the customer conversation.'
				);
				return;
			}

			const { customer, customerConversation } = response.data;
			dispatch(addConversationToCustomerConversations(customerConversation, true));
			dispatch(setCustomer(customer || customerConversation._customer));
			dispatch(setStatus('connecting'));
			dispatch(setVisible(true));
			onOpenChange(false);
			resetDialer();
		} catch (error) {
			console.error('Error starting dialer call:', error);
			toast.error('Unable to place call. Please try again.');
		} finally {
			setIsCalling(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent
				mobileVariant="fullscreen"
				className="w-[calc(100vw-2rem)] max-w-sm gap-5 max-md:flex max-md:flex-col max-md:gap-4 max-md:px-4"
				onKeyDown={handleDialogKeyDown}
				onOpenAutoFocus={(event) => {
					if (!isMobile) return;
					event.preventDefault();
					(event.currentTarget as HTMLElement | null)?.focus?.();
				}}
			>
				<DialogHeader>
					<DialogTitle>New Call</DialogTitle>
				</DialogHeader>

				<VoiceAgreementBanner />

				<div className="space-y-4 max-md:mx-auto max-md:my-auto max-md:w-full max-md:max-w-xs">
					<div className="rounded-2xl border bg-muted/30 p-4">
						<div className="mb-3 flex items-center justify-center">
							<div className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
								<Phone className="size-5" />
							</div>
						</div>
						<Input
							value={displayNumber}
							onChange={(event) => handleNumberChange(event.target.value)}
							placeholder="Enter phone number"
							aria-label="Phone number"
							inputMode={isMobile ? 'none' : 'tel'}
							type="tel"
							autoComplete="off"
							enterKeyHint="go"
							disabled={isCalling}
							className="h-12 border-0 bg-transparent text-center text-2xl font-light tracking-wide shadow-none focus-visible:ring-0"
						/>
						{validationMessage && (
							<p className="mt-2 text-center text-sm text-destructive">
								{validationMessage}
							</p>
						)}
						{!validationMessage && customerCallInProgress && (
							<p className="mt-2 text-center text-sm text-muted-foreground">
								This customer is already on a call with a teammate.
							</p>
						)}
					</div>

					<div className="grid grid-cols-3 gap-3">
						{KEYPAD_ROWS.flat().map(({ digit, letters }) => (
							<Button
								key={digit}
								type="button"
								variant="secondary"
								disabled={isCalling}
								onClick={() => handleKeyPress(digit)}
								className="h-16 rounded-full text-2xl font-light max-md:h-[clamp(3rem,calc(var(--app-height)*0.085),4.5rem)]"
							>
								<span className="flex flex-col items-center leading-none">
									<span>{digit}</span>
									<span className="mt-1 h-3 text-[10px] font-semibold tracking-[0.18em] text-muted-foreground">
										{letters}
									</span>
								</span>
							</Button>
						))}
					</div>

					<div className="grid grid-cols-3 items-center gap-3">
						<div />
						<Button
							type="button"
							disabled={!dialedNumber || isCalling || isCallInProgress}
							onClick={handleStartCall}
							aria-label="Call"
							className="h-14 rounded-full bg-green-600 text-white hover:bg-green-700"
						>
							{isCalling ? (
								<Loader2 className="size-5 animate-spin" />
							) : (
								<Phone className="size-5" />
							)}
						</Button>
						<Button
							type="button"
							variant="ghost"
							disabled={!dialedNumber || isCalling}
							onClick={handleBackspace}
							aria-label="Delete last digit"
							className={cn(
								"ml-auto h-11 w-11 rounded-full",
								!dialedNumber && "opacity-0"
							)}
						>
							<Delete className="size-5" />
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
